import { useCallback, useEffect, useState } from 'react';
import classnames from 'clsx';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { SegmentedControl } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_DURATIONS } from 'ui-components/services/notification-service';

import { FulfillmentPlanFulfillmentType } from '../../../../../../enums/fulfillment-plan-fulfillment-type';
import Global from '../../../../../../store/global-selectors';
import { setFulfillmentTypeFreight, setFulfillmentTypeParcel } from '../../../../../../store/ltl/ltl.actions';

import { messages } from './messages';
import { FlexRow, Gap } from 'ui-components/components/flex';
import styles from './shipment-type-toggle.module.css';
import { useEnableLtlFreight } from '../../../../../../feature-flags/hooks/use-enable-ltl-freight';

type ShipmentTypeToggleProps = {
  fulfillmentPlanId: string | undefined;
  className?: string;
};

export const ShipmentTypeToggle = ({ fulfillmentPlanId, className }: ShipmentTypeToggleProps) => {
  const isLtlFreightEnabled = useEnableLtlFreight();
  const intl = useIntl();
  const dispatch = useDispatch();

  const fulfillmentType = useSelector((state) => Global.selectEntitiesFulfillmentPlanFulfillmentType(state, { fulfillmentPlanId }));
  const isLoading = useSelector((state) => Global.selectLtlIsFulfillmentTypeChanging(state, {}));
  const hasError = useSelector((state) => Global.selectLtlHasFulfillmentTypeError(state, {}));

  const packageLabel = intl.formatMessage(messages.packageOption);
  const freightLabel = intl.formatMessage(messages.freightOption);

  const reduxDerivedValue = fulfillmentType === FulfillmentPlanFulfillmentType.FreightLtl ? freightLabel : packageLabel;

  const [activeValue, setActiveValue] = useState(reduxDerivedValue);
  const [loadingValue, setLoadingValue] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading) {
      setActiveValue(reduxDerivedValue);
      setLoadingValue(null);
    }
  }, [isLoading, reduxDerivedValue]);

  useEffect(() => {
    if (hasError) {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        message: intl.formatMessage(messages.updateFailed),
        duration: NOTIFICATION_DURATIONS.SHORT,
      });
    }
  }, [hasError, intl]);

  const handleChange = useCallback(
    (value: string) => {
      if (value === activeValue || !fulfillmentPlanId) return;
      setActiveValue(value);
      setLoadingValue(value);
      if (value === freightLabel) {
        dispatch(setFulfillmentTypeFreight(fulfillmentPlanId));
      } else {
        dispatch(setFulfillmentTypeParcel(fulfillmentPlanId));
      }
    },
    [activeValue, dispatch, freightLabel, fulfillmentPlanId],
  );

  const makeLabel = (label: string) =>
    loadingValue === label ? (
      <FlexRow className={styles['loading-label']} gap={Gap.Space4}>
        <FontAwesomeIcon icon={faSpinner} spin />
        {label}
      </FlexRow>
    ) : (
      label
    );

  if (!isLtlFreightEnabled) {
    return null;
  }

  return (
    <SegmentedControl
      className={classnames(styles.container, className)}
      data={[
        { value: packageLabel, label: makeLabel(packageLabel), disabled: isLoading },
        { value: freightLabel, label: makeLabel(freightLabel), disabled: isLoading },
      ]}
      fullWidth
      onChange={handleChange}
      value={activeValue}
    />
  );
};
